import React, { useState } from 'react';
import { Award, Printer, Search } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';
import { formatDate } from '../../utils/helpers';
import DateInput from '../../components/common/DateInput';

const today = () => new Date().toISOString().slice(0, 10);

function toWords(n) {
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
    'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  if (!n || isNaN(n)) return '';
  n = parseInt(n);
  if (n < 20) return ones[n];
  if (n < 100) return tens[Math.floor(n/10)] + (n%10 ? ' ' + ones[n%10] : '');
  return ones[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' ' + toWords(n%100) : '');
}

function getAge(dob) {
  if (!dob) return '';
  const diff = Date.now() - new Date(dob).getTime();
  const years = Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  return years;
}

async function printStudyCertificate(student, classes, issueDate) {
  const school = await getSchoolInfo();
  const cls = classes.find(c => c.id === student.classId || c.id === student.class_id);
  const className = cls?.name || student.className || student.class_name || '—';
  const age = getAge(student.dob);
  const ageWords = age ? `(${toWords(age)} Years)` : '';
  const win = window.open('', '_blank', 'width=750,height=600');
  win.document.open();
  win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/>
  <title>Study Certificate</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Times New Roman', serif; font-size: 15px; color: #111; padding: 24px 32px; }
    .border-box { border: 3px double #6b21a8; padding: 24px 28px; min-height: 90vh; position: relative; }
    .cert-no { font-size: 12px; color: #555; margin-bottom: 8px; }
    .body-text { line-height: 2.2; font-size: 15px; margin: 24px 0; text-align: justify; }
    .underline { border-bottom: 1px solid #000; display: inline-block; min-width: 180px; text-align: center; font-weight: 700; padding: 0 4px; }
    .sign-block { margin-top: 48px; display: flex; justify-content: space-between; font-size: 14px; }
    .sign-block div { text-align: center; }
    .sign-line { border-top: 1px solid #000; width: 160px; margin: 0 auto 4px; }
    .footer-note { font-size: 12px; color: #555; text-align: center; margin-top: 24px; border-top: 1px solid #ddd; padding-top: 8px; }
    @media print { .no-print { display: none !important; } @page { margin: 3mm; } * { color:#000 !important; } }
    .print-btn { text-align: center; margin: 20px 0 0; }
  </style></head><body>
  <div class="border-box">
    ${schoolHeader(school, '#6b21a8', 'Study Certificate')}
    <div class="cert-no">Cert. No: SC-${Date.now().toString().slice(-6)} &nbsp;&nbsp; Date: ${formatDate(issueDate)}</div>
    <div class="body-text">
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;This is to certify that
      <span class="underline">&nbsp;${student.firstName || student.first_name} ${student.lastName || student.last_name}&nbsp;</span>,
      ${student.gender === 'girl' ? 'D/o' : 'S/o'}
      <span class="underline">&nbsp;${student.fatherName || student.father_name || '—'}&nbsp;</span>,
      is a <em>bonafide student</em> of this school studying in
      <span class="underline">&nbsp;Class ${className}&nbsp;</span>
      during the academic year <span class="underline">&nbsp;${new Date(issueDate).getFullYear()}-${new Date(issueDate).getFullYear() + 1}&nbsp;</span>.
      ${student.dob ? `${student.gender === 'girl' ? 'Her' : 'His'} Date of Birth as per school records is <span class="underline">&nbsp;${formatDate(student.dob)}&nbsp;</span> and age is <span class="underline">&nbsp;${age} Years ${ageWords}&nbsp;</span>.` : ''}
      ${student.admissionNumber || student.admission_number ? `${student.gender === 'girl' ? 'Her' : 'His'} Admission Number is <span class="underline">&nbsp;${student.admissionNumber || student.admission_number}&nbsp;</span>.` : ''}
    </div>
    <div class="body-text">
      This certificate is issued for the purpose of
      <span class="underline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
      on request of the student / parent.
    </div>
    <div class="sign-block">
      <div><div class="sign-line"></div>Class Teacher</div>
      <div><div class="sign-line"></div>Principal / Headmaster</div>
    </div>
    <div class="footer-note">This is a computer-generated certificate — ${school.name}</div>
  </div>
  <div class="print-btn no-print">
    <button onclick="window.print()" style="background:#6b21a8;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:14px;cursor:pointer">Print Certificate</button>
  </div>
  </body></html>`);
  win.document.close();
}

async function printConductCertificate(student, classes, issueDate, conduct) {
  const school = await getSchoolInfo();
  const cls = classes.find(c => c.id === student.classId || c.id === student.class_id);
  const className = cls?.name || student.className || student.class_name || '—';
  const win = window.open('', '_blank', 'width=750,height=600');
  win.document.open();
  win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/>
  <title>Conduct Certificate</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Times New Roman', serif; font-size: 15px; color: #111; padding: 24px 32px; }
    .border-box { border: 3px double #1e3a8a; padding: 24px 28px; min-height: 90vh; }
    .cert-no { font-size: 12px; color: #555; margin-bottom: 8px; }
    .body-text { line-height: 2.2; font-size: 15px; margin: 24px 0; text-align: justify; }
    .underline { border-bottom: 1px solid #000; display: inline-block; min-width: 180px; text-align: center; font-weight: 700; padding: 0 4px; }
    .conduct-badge { display: inline-block; background: #f0fdf4; border: 1px solid #16a34a; color: #15803d; font-weight: 700; font-size: 16px; padding: 2px 14px; border-radius: 4px; }
    .sign-block { margin-top: 48px; display: flex; justify-content: space-between; font-size: 14px; }
    .sign-block div { text-align: center; }
    .sign-line { border-top: 1px solid #000; width: 160px; margin: 0 auto 4px; }
    .footer-note { font-size: 12px; color: #555; text-align: center; margin-top: 24px; border-top: 1px solid #ddd; padding-top: 8px; }
    @media print { .no-print { display: none !important; } @page { margin: 3mm; } * { color:#000 !important; } }
    .print-btn { text-align: center; margin: 20px 0 0; }
  </style></head><body>
  <div class="border-box">
    ${schoolHeader(school, '#1e3a8a', 'Conduct Certificate')}
    <div class="cert-no">Cert. No: CC-${Date.now().toString().slice(-6)} &nbsp;&nbsp; Date: ${formatDate(issueDate)}</div>
    <div class="body-text">
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;This is to certify that
      <span class="underline">&nbsp;${student.firstName || student.first_name} ${student.lastName || student.last_name}&nbsp;</span>,
      ${student.gender === 'girl' ? 'D/o' : 'S/o'}
      <span class="underline">&nbsp;${student.fatherName || student.father_name || '—'}&nbsp;</span>,
      was / is a student of <strong>${school.name}</strong> in
      <span class="underline">&nbsp;Class ${className}&nbsp;</span>.
      ${student.admissionNumber || student.admission_number ? `${student.gender === 'girl' ? 'Her' : 'His'} Admission Number is <span class="underline">&nbsp;${student.admissionNumber || student.admission_number}&nbsp;</span>.` : ''}
    </div>
    <div class="body-text">
      During ${student.gender === 'girl' ? 'her' : 'his'} stay in this institution,
      ${student.gender === 'girl' ? 'her' : 'his'} conduct and character have been found to be
      <span class="conduct-badge">&nbsp;${conduct}&nbsp;</span>.
      ${student.gender === 'girl' ? 'She' : 'He'} has been sincere, disciplined, and well-behaved throughout.
      We wish ${student.gender === 'girl' ? 'her' : 'him'} all the best in future endeavours.
    </div>
    <div class="body-text">
      This certificate is issued on request of the student / parent for the purpose of
      <span class="underline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>.
    </div>
    <div class="sign-block">
      <div><div class="sign-line"></div>Class Teacher</div>
      <div><div class="sign-line"></div>Principal / Headmaster</div>
    </div>
    <div class="footer-note">This is a computer-generated certificate — ${school.name}</div>
  </div>
  <div class="print-btn no-print">
    <button onclick="window.print()" style="background:#1e3a8a;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:14px;cursor:pointer">Print Certificate</button>
  </div>
  </body></html>`);
  win.document.close();
}

export default function Certificates() {
  const { students, classes } = useApp();
  const { currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [type, setType] = useState('study');
  const [issueDate, setIssueDate] = useState(today());
  const [conduct, setConduct] = useState('Good');

  if (currentUser?.role !== 'admin') {
    return (
      <div className="p-8 text-center text-gray-500">
        <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Certificates are available for Admin only.</p>
      </div>
    );
  }

  const filtered = students.filter(s => {
    const q = search.toLowerCase();
    const name = `${s.firstName || s.first_name || ''} ${s.lastName || s.last_name || ''}`.toLowerCase();
    const adm = (s.admissionNumber || s.admission_number || '').toLowerCase();
    return name.includes(q) || adm.includes(q);
  }).slice(0, 50);

  const handlePrint = async () => {
    if (!selected) return;
    if (type === 'study') await printStudyCertificate(selected, classes, issueDate);
    else await printConductCertificate(selected, classes, issueDate, conduct);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Award className="w-7 h-7 text-purple-700" />
        <h1 className="text-2xl font-bold text-gray-800">Certificates</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left — student picker */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-600 mb-3">Select Student</p>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              placeholder="Search by name or admission no…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
            {filtered.length === 0 && (
              <p className="text-sm text-gray-400 py-4 text-center">No students found</p>
            )}
            {filtered.map(s => {
              const name = `${s.firstName || s.first_name || ''} ${s.lastName || s.last_name || ''}`.trim();
              const cls = classes.find(c => c.id === (s.classId || s.class_id));
              const isSelected = selected?.id === s.id;
              return (
                <div
                  key={s.id}
                  onClick={() => setSelected(s)}
                  className={`flex items-center gap-3 px-2 py-2.5 cursor-pointer rounded-lg transition ${isSelected ? 'bg-purple-50 border border-purple-200' : 'hover:bg-gray-50'}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${isSelected ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    {name[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{name}</p>
                    <p className="text-xs text-gray-400">{cls?.name || '—'} &nbsp;·&nbsp; {s.admissionNumber || s.admission_number || 'No Adm. No'}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right — certificate options */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
          <p className="text-sm font-semibold text-gray-600">Certificate Options</p>

          {/* Type */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Certificate Type</p>
            <div className="flex gap-3">
              {['study', 'conduct'].map(t => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border transition ${type === t ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'}`}
                >
                  {t === 'study' ? '📄 Study Certificate' : '🏅 Conduct Certificate'}
                </button>
              ))}
            </div>
          </div>

          {/* Conduct grade */}
          {type === 'conduct' && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Conduct Grade</p>
              <div className="flex gap-2 flex-wrap">
                {['Excellent', 'Very Good', 'Good', 'Satisfactory'].map(g => (
                  <button
                    key={g}
                    onClick={() => setConduct(g)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition ${conduct === g ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300 hover:border-green-400'}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Issue date */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Issue Date</p>
            <DateInput
              value={issueDate}
              onChange={e => setIssueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
          </div>

          {/* Selected student preview */}
          {selected && (
            <div className="bg-purple-50 border border-purple-100 rounded-lg p-3 text-sm">
              <p className="font-semibold text-purple-800">
                {`${selected.firstName || selected.first_name || ''} ${selected.lastName || selected.last_name || ''}`.trim()}
              </p>
              <p className="text-purple-600 text-xs mt-0.5">
                {classes.find(c => c.id === (selected.classId || selected.class_id))?.name || '—'}
                &nbsp;·&nbsp;
                {selected.admissionNumber || selected.admission_number || 'No Adm. No'}
              </p>
            </div>
          )}

          <button
            onClick={handlePrint}
            disabled={!selected}
            className="mt-auto flex items-center justify-center gap-2 w-full py-3 rounded-lg text-sm font-bold transition bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Printer className="w-4 h-4" />
            Print {type === 'study' ? 'Study' : 'Conduct'} Certificate
          </button>
        </div>
      </div>
    </div>
  );
}
